import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Concept-verwijsbrief (ADR 0017).
 *
 * Firestore path: tenants/{tenantId}/users/{userId}/verwijsbrieven/{id}
 *
 * Bevat nooit patiëntidentiteit: de POH'er kopieert de inhoud naar de brief in
 * het HIS (of ZorgDomein), en daar staan patiënt, praktijk en verwijzer al
 * (besluit Jos 12-10-2026). Wat hier staat is de inhoud uit het
 * gepseudonimiseerde consult. Een concept
 * leeft 72 uur vanaf aanmaken, net als de Sara-gegevens; een Firestore
 * TTL-policy op `expiresAt` ruimt op, de service filtert verlopen documenten
 * bij het lezen omdat TTL tot 24 uur na de deadline mag uitlopen.
 */
export const VERWIJSBRIEF_TTL_UUR = 72;
export const VerwijsbriefBron = z.enum(['sara', 'mila', 'handmatig']);
const Kort = z.string().trim().max(200);
const Lang = z.string().max(6000);
/** Ontvangende zorgaanbieder, overgenomen uit de Sociale Kaart of met de hand. */
export const VerwijsbriefGeadresseerde = z.object({
    providerId: DocId.optional(),
    naam: Kort,
    adres: Kort.default(''),
});
/**
 * Alleen wat Mila uit het gesprek kan samenvatten. Soort verwijzing, ernst,
 * diagnose en DSM-classificatie kiest de POH in het HIS of ZorgDomein zelf;
 * die hier ook laten kiezen was dubbelwerk (besluit Jos 13-10-2026).
 */
export const VerwijsbriefInhoud = z.object({
    klachten: Lang.default(''),
    hulpvraag: Lang.default(''),
    eerdereStappen: Lang.default(''),
});
const Velden = {
    opstellerNaam: Kort.default(''),
    geadresseerde: VerwijsbriefGeadresseerde.nullable().default(null),
    inhoud: VerwijsbriefInhoud.default({}),
};
export const VerwijsbriefSchema = z.object({
    id: DocId,
    tenantId: TenantId,
    userId: DocId,
    bron: VerwijsbriefBron,
    ...Velden,
    // Server-gestempeld: de inhoud komt van Mila (AI Act art. 50). Zolang
    // `nagelezen` false is, staat er een AI-label en kan er niets gekopieerd
    // worden (besluit Jos 12-10-2026).
    uitTranscript: z.boolean().default(false),
    nagelezen: z.boolean().default(false),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    expiresAt: IsoTimestamp,
});
export const CreateVerwijsbriefInput = z.object({ bron: VerwijsbriefBron, ...Velden }).strict();
/** Nieuw concept waarvan de inhoud uit het transcript van een eigen Sara-opname komt. */
export const VerwijsbriefUitSaraInput = CreateVerwijsbriefInput.omit({ inhoud: true }).extend({
    saraTaskId: DocId,
});
// Geen defaults bij bijwerken: een ontbrekend veld blijft wat het was.
export const UpdateVerwijsbriefInput = z
    .object({
    opstellerNaam: Kort,
    geadresseerde: VerwijsbriefGeadresseerde.nullable(),
    inhoud: VerwijsbriefInhoud,
    nagelezen: z.boolean(),
})
    .partial()
    .strict();
//# sourceMappingURL=verwijsbrief.js.map